/**
 * Collapsible side panel — right side of the map.
 * Tab bar at the top (one tab per selected flight) + content area rendered by FlightTab.
 * A drag handle on the left edge (expanded state only) lets the user resize the panel
 * within [300, 450] px. Width is written to prefs.panelWidth on pointerup.
 * Not rendered when no flights are selected.
 */

import { useRef, useCallback } from 'react';
import type { MutableRefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import { useAppStore, getFlightColor } from '../store/appStore';
import { IconMapping } from '../constants/iconMapping';
import { saveSelection } from '../utils/cookieStore';
import FlightTab from './FlightTab';
import styles from './SidePanel.module.css';

const MIN_WIDTH = 300;
const MAX_WIDTH = 450;

interface Props {
  /** Shared react-map-gl MapRef forwarded to FlightTab zoom helpers. */
  mapRef: MutableRefObject<MapRef | null>;
}

/**
 * Right-side overlay panel with per-flight tabs and a resize handle.
 * Returns null when no flights are selected.
 * @param mapRef - ref to the react-map-gl MapRef instance
 */
export default function SidePanel({ mapRef }: Props) {
  const {
    selectedFlightIds,
    flights,
    flightPrefs,
    prefs,
    isPanelOpen,
    activePanelFlightId,
    setPanelOpen,
    setActivePanelFlight,
    setPrefs,
    deselectFlight,
  } = useAppStore();

  const panelRef = useRef<HTMLDivElement>(null);

  // ── Drag-resize handle (pointer capture) ─────────────────────────────────────
  // These useCallback hooks MUST be declared before the early return below so that
  // React always calls the same number of hooks regardless of selectedFlightIds.length.

  /**
   * Live-update panel width during drag via inline style (avoids cookie churn).
   */
  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!(e.buttons & 1)) return;
    const w = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(window.innerWidth - e.clientX)));
    if (panelRef.current) panelRef.current.style.width = `${w}px`;
  }, []);

  /**
   * On drag end: persist the final width to prefs and the cookie.
   */
  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const w = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(window.innerWidth - e.clientX)));
    setPrefs({ panelWidth: w });
  }, [setPrefs]);

  // Early return — must come AFTER all hook calls.
  if (selectedFlightIds.length === 0) return null;

  const activeId =
    activePanelFlightId !== null && selectedFlightIds.includes(activePanelFlightId)
      ? activePanelFlightId
      : selectedFlightIds[0];

  const CollapseIcon = IconMapping.panel_collapse;
  const ExpandIcon   = IconMapping.panel_expand;
  const CloseTabIcon = IconMapping.panel_close_tab;

  /**
   * Closes (deselects) a flight tab, switches the active tab if needed,
   * and persists the updated selection to the cookie.
   * @param id - flightId to close
   */
  const handleCloseTab = (id: number) => {
    const newIds = selectedFlightIds.filter((fid) => fid !== id);
    const newPerFlight: Record<number, { showNormal: boolean; showInstant: boolean; showTeams: boolean; isVisible: boolean }> = {};
    for (const fid of newIds) {
      const fp = flightPrefs[fid];
      if (fp) newPerFlight[fid] = fp;
    }
    if (!useAppStore.getState().isTourDemo) {
      saveSelection({ selectedFlightIds: newIds, perFlight: newPerFlight });
    }

    if (id === activeId) {
      const next = newIds[0] ?? null;
      setActivePanelFlight(next);
      if (next === null) setPanelOpen(false);
    }
    deselectFlight(id);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  const panelStyle = isPanelOpen ? { width: `${prefs.panelWidth}px` } : undefined;

  return (
    <div
      ref={panelRef}
      className={`${styles.panel} ${isPanelOpen ? styles.open : styles.collapsed}`}
      style={panelStyle}
    >
      {/* ── Drag handle — left edge, expanded only ──────────────────────── */}
      {isPanelOpen && (
        <div
          className={styles.resizeHandle}
          onPointerDown={(e) => e.currentTarget.setPointerCapture(e.pointerId)}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        />
      )}

      {/* ── Tab bar — always visible ─────────────────────── */}
      <div className={styles.tabBar} data-tour="tour-flight-tabs">
        <button
          className={styles.toggleBtn}
          onClick={() => setPanelOpen(!isPanelOpen)}
          title={isPanelOpen ? 'Réduire le panneau' : 'Développer le panneau'}
        >
          {isPanelOpen ? <CollapseIcon size={16} /> : <ExpandIcon size={16} />}
        </button>

        {isPanelOpen &&
          selectedFlightIds.map((id) => {
            const color      = getFlightColor(id);
            const flight     = flights[id];
            const isActive   = id === activeId;

            return (
              <button
                key={id}
                className={`${styles.flightTab} ${isActive ? styles.flightTabActive : ''}`}
                onClick={() => setActivePanelFlight(id)}
                title={flight ? `Vol #${flight.id}` : `Vol ${id}`}
              >
                <span className={styles.tabDot} style={{ background: color }} />
                <span className={styles.tabLabel}>Vol {flight?.id ?? id}</span>
                <span
                  className={styles.closeTab}
                  role="button"
                  tabIndex={0}
                  title="Fermer ce vol"
                  onClick={(e) => { e.stopPropagation(); handleCloseTab(id); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleCloseTab(id); } }}
                >
                  <CloseTabIcon size={12} />
                </span>
              </button>
            );
          })}
      </div>

      {/* ── Scrollable content area ───────────────────────── */}
      {isPanelOpen && (
        <div className={`${styles.content} scrollable`}>
          <FlightTab flightId={activeId} mapRef={mapRef} />
        </div>
      )}
    </div>
  );
}
